import { formatJalaliDate } from '@bookora/shared';

import type { AppointmentResponse } from '@/api/generated/model';

/**
 * ════════════════════════════════════════════════════════════════════════════
 *  «وصل به نوبت ساعت ۱۶:۴۵» — the visit this treatment most likely belongs to
 * ════════════════════════════════════════════════════════════════════════════
 *
 * «ثبت جلسه» from the patient file (or from the list) carries a patient and
 * nothing else. The patient was very often booked for exactly this service
 * today, and a session recorded without that link leaves the appointment
 * «تأییدشده» for ever: the file keeps announcing it as the next visit, the
 * calendar shows it as still to come, and tomorrow it reads as a no-show.
 *
 * So once a patient and a service are chosen, the form looks for an OPEN
 * appointment of that patient for that service, from yesterday to tomorrow,
 * that has no session yet, and offers it switched on. Linking is what makes
 * the server move the appointment to DONE when the treatment is completed —
 * the same `closeAppointment` path a session started from the calendar takes.
 *
 * Open means CONFIRMED, ARRIVED or IN_PROGRESS. A REQUESTED booking was never
 * accepted by the clinic, and CANCELLED / NO_SHOW released their slot (the
 * server refuses to hang a session off those). DONE already has its session.
 *
 * The nearest one in time wins: a patient with a 10:00 and a 16:45 filler
 * today is being treated for whichever is closer to now.
 */

export const OPEN_APPOINTMENT_STATUSES = ['CONFIRMED', 'ARRIVED', 'IN_PROGRESS'] as const;

const DAY_MS = 86_400_000;

/**
 * The window asked of `GET /appointments`: generous on purpose (the browser's
 * day may not be Tehran's), and narrowed to Tehran's yesterday–tomorrow by
 * {@link nearbyDayWord} afterwards.
 */
export function nearbyWindow(now: Date): { from: string; to: string } {
  return {
    from: new Date(now.getTime() - 2 * DAY_MS).toISOString(),
    to: new Date(now.getTime() + 2 * DAY_MS).toISOString(),
  };
}

export type NearbyDay = 'دیروز' | 'امروز' | 'فردا';

/** «امروز» / «دیروز» / «فردا» on Tehran's calendar, or null outside those three days. */
export function nearbyDayWord(startAt: string | Date, now: Date): NearbyDay | null {
  const day = formatJalaliDate(new Date(startAt));
  if (day === formatJalaliDate(now)) return 'امروز';
  if (day === formatJalaliDate(new Date(now.getTime() - DAY_MS))) return 'دیروز';
  if (day === formatJalaliDate(new Date(now.getTime() + DAY_MS))) return 'فردا';
  return null;
}

/**
 * The appointment to offer, or null. `procedureId` is the CATALOGUE id — what
 * an appointment's procedure lines carry.
 */
export function matchNearbyAppointment(
  appointments: readonly AppointmentResponse[],
  procedureId: string | null,
  now: Date,
): AppointmentResponse | null {
  if (!procedureId) return null;
  const open = new Set<string>(OPEN_APPOINTMENT_STATUSES);
  const candidates = appointments.filter(
    (appointment) =>
      open.has(appointment.status) &&
      !appointment.sessionId &&
      appointment.procedures.some((line) => line.procedureId === procedureId) &&
      nearbyDayWord(appointment.startAt, now) !== null,
  );
  if (candidates.length === 0) return null;
  const distance = (appointment: AppointmentResponse): number =>
    Math.abs(new Date(appointment.startAt).getTime() - now.getTime());
  return candidates.reduce((best, next) => (distance(next) < distance(best) ? next : best));
}
